import {
  ArrowDown,
  ArrowRight,
  Check,
  Film,
  Users,
  HeartHandshake,
  Earth,
  Armchair,
  CircleFadingArrowUp,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import { WaitlistTrigger } from '../components/WaitlistTrigger';

const filmmakerFeatures = [
  {
    icon: Earth,
    number: '01',
    title: 'Make Connections',
    description:
      'Connect with other filmmakers and film festivals to build your network then keep up with them on your home feed.',
    screenshot: '/images/screenshots/home-feed.png',
    screenshotAlt:
      'Keep up with your filmmaker and film festival connections on the home feed page',
  },
  {
    icon: Armchair,
    number: '02',
    title: 'Find Fests & Submit',
    description:
      'Use our powerful filter options to browse our repository of verified festivals to submit your film to.',
    screenshot: '/images/screenshots/filmmaker-find-festivals.png',
    screenshotAlt: 'Browse and filter verified festivals on Filmspo',
  },
  {
    icon: HeartHandshake,
    number: '03',
    title: 'Join Communities',
    description: 'Find like-minded independent film buffs using our communities page.',
    screenshot: '/images/screenshots/community-page2.png',
    screenshotAlt: 'Track your festival submissions and decisions on Filmspo',
  },
];

const festivalFeatures = [
  {
    icon: Film,
    title: 'Comprehensive Festival Management',
    description:
      'Submission governance, team building, attendance tracking, messaging, finances and more - all in one place.',
    screenshot: '/images/screenshots/fest-portal.png',
    screenshotAlt: 'Filmspo festival portal overview dashboard',
  },
  {
    icon: Users,
    title: 'Build Your Community',
    description:
      'Create a community officially linked to your festival so you can better engage filmmakers and fans.',
    screenshot: '/images/screenshots/fest-community.png',
    screenshotAlt: 'Festival community page on Filmspo',
  },
  {
    icon: CircleFadingArrowUp,
    title: 'Build your profile',
    description:
      "Post announcements, events, polls and more that will appear in your festival followers' home feed.",
    screenshot: '/images/screenshots/fest-profile.png',
    screenshotAlt: 'Festival follower feed and announcements on Filmspo',
  },
];

const journey = [
  {
    number: '01',
    title: 'Build your profile',
    description:
      'Show other filmmakers and film festivals who you are by building out your Filmspo profile. Your profile will show the film world your public projects, your accolades, and much more.',
    screenshot: '/images/screenshots/filmmaker-profile.png',
    screenshotAlt: 'Building your filmmaker profile on Filmspo',
  },
  {
    number: '02',
    title: 'Check-in to festivals',
    description:
      'Show support to the festivals you love by checking in with a festival code. These festival check-ins will appear on your profile and count toward Filmspo badges and rewards!',
    screenshot: '/images/screenshots/checkin-view.png',
    screenshotAlt: 'Browsing verified festivals on Filmspo',
  },
  {
    number: '03',
    title: 'Post for your network',
    description:
      'Posting to Filmspo works much like other social platforms. Keep your network in the loop, post a poll for feedback, or share your latest project!',
    screenshot: '/images/screenshots/create-post.png',
    screenshotAlt: 'Submitting a project to a festival on Filmspo',
  },
  {
    number: '04',
    title: 'Get the latest news',
    description:
      'Start your own blog and stay up to day with the latest film and festival-related news coming from a wide host of sources such as The Hollywood Reporter, Variety, and No Film School.',
    screenshot: '/images/screenshots/article-page.png',
    screenshotAlt: 'Film-related news on Filmspo',
  },
  {
    number: '05',
    title: 'Direct message',
    description:
      'Talk directly with other filmmakers or film festivals with direct messaging. Filmspo also supports group messaging for teams looking to coordinate.',
    screenshot: '/images/screenshots/inbox-page.png',
    screenshotAlt: 'Submitting a project to a festival on Filmspo',
  },
  {
    number: '06',
    title: 'Film-centric job boards',
    description:
      'Need an assistant director to round out your team? Want some more experience on the acting side of things? Post or book open roles near you on the job board.',
    screenshot: '/images/screenshots/job-board.png',
    screenshotAlt: 'Submitting a project to a festival on Filmspo',
  },
  {
    number: '07',
    title: 'Show off your work',
    description:
      'Filmspo has a streaming page where we highlight some of our favorite public projects across the site. Submit your film for our stream team to review and highlight!',
    screenshot: '/images/screenshots/watch-page.png',
    screenshotAlt: 'Submitting a project to a festival on Filmspo',
  },
  {
    number: '08',
    title: 'Find funding',
    description:
      'Filmspo pulls in grants and funding programs from across the internet for you to see so you can get your dream project financed.',
    screenshot: '/images/screenshots/grants-page.png',
    screenshotAlt: 'Submitting a project to a festival on Filmspo',
  },
];

const heroVideos = [
  'https://filmspo-prod.nyc3.cdn.digitaloceanspaces.com/videos/raw/hero.mp4',
  'https://filmspo-prod.nyc3.cdn.digitaloceanspaces.com/videos/raw/hero2.mp4',
  'https://filmspo-prod.nyc3.cdn.digitaloceanspaces.com/videos/raw/hero3.mp4',
  'https://filmspo-prod.nyc3.cdn.digitaloceanspaces.com/videos/raw/hero4.mp4',
];

export function HomePage() {
  const [activeFilmmaker, setActiveFilmmaker] = useState(0);
  const [activeSlot, setActiveSlot] = useState<0 | 1>(0);
  const heroIndexRef = useRef(0);
  const videoRef0 = useRef<HTMLVideoElement>(null);
  const videoRef1 = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const activeRef = activeSlot === 0 ? videoRef0 : videoRef1;
    const inactiveSlot = (1 - activeSlot) as 0 | 1;
    const inactiveRef = inactiveSlot === 0 ? videoRef0 : videoRef1;

    activeRef.current?.play();

    const timer = setTimeout(() => {
      heroIndexRef.current = (heroIndexRef.current + 1) % heroVideos.length;
      const el = inactiveRef.current;
      if (el) {
        el.src = heroVideos[heroIndexRef.current]!;
        el.load();
        el.play();
      }
      setActiveSlot(inactiveSlot);
    }, 5000);

    return () => clearTimeout(timer);
  }, [activeSlot]);

  useEffect(() => {
    [...filmmakerFeatures, ...festivalFeatures].forEach(f => {
      const img = new Image();
      img.src = f.screenshot;
    });
  }, []);
  const [activeFestival, setActiveFestival] = useState(0);
  const [activeJourney, setActiveJourney] = useState(0);

  const activeFilmmakerFeature = (filmmakerFeatures[activeFilmmaker] ?? filmmakerFeatures[0])!;
  const activeFestivalFeature = (festivalFeatures[activeFestival] ?? festivalFeatures[0])!;

  function prevJourney() {
    setActiveJourney(i => (i - 1 + journey.length) % journey.length);
  }
  function nextJourney() {
    setActiveJourney(i => (i + 1) % journey.length);
  }

  return (
    <div className="home-page">
      <section className="cinema-hero" aria-labelledby="hero-heading">
        <video
          ref={videoRef0}
          className="cinema-hero-image"
          src={heroVideos[0]!}
          autoPlay
          muted
          playsInline
          aria-hidden="true"
          style={{ opacity: activeSlot === 0 ? 1 : 0 }}
        />
        <video
          ref={videoRef1}
          className="cinema-hero-image"
          muted
          playsInline
          aria-hidden="true"
          style={{ opacity: activeSlot === 1 ? 1 : 0 }}
        />
        <div className="cinema-hero-shade" aria-hidden="true" />
        <div className="cinema-grain" aria-hidden="true" />

        <div className="cinema-hero-content page-container">
          <div className="hero-copy">
            <p className="section-kicker hero-kicker">
              <span>Filmspo presents</span>
              The independent filmmaker & film festival platform
            </p>
            <h1 id="hero-heading">
              <span>where filmmakers & film festivals</span>
              <em>connect, create, submit.</em>
            </h1>
            <p className="hero-description">
              Free accounts, community driven design, verified festivals, film-centric job board,
              and powerful tools for festival organizers and filmmakers.
            </p>
            <div className="hero-actions">
              <WaitlistTrigger source="hero" className="button button-gold button-large">
                Join the Waitlist
                <ArrowRight aria-hidden="true" />
              </WaitlistTrigger>
              {/* <a className="button button-glass button-large" href="#filmmakers">
                <Play aria-hidden="true" />
                See the features
              </a> */}
            </div>
            <div className="hero-proof" aria-label="Filmspo benefits">
              <span>
                <Check aria-hidden="true" /> Film-centered social platform
              </span>
              <span>
                <Check aria-hidden="true" /> Verified festivals
              </span>
              <span>
                <Check aria-hidden="true" /> Comprehensive festival management
              </span>
            </div>
          </div>
        </div>

        <a className="hero-scroll" href="#filmmakers" aria-label="Scroll to filmmaker features">
          <span>Continue</span>
          <ArrowDown aria-hidden="true" />
        </a>
      </section>

      <section id="filmmakers" className="filmmaker-section section-shell">
        <div className="page-container">
          <div className="section-heading editorial-heading">
            <p className="section-kicker">
              <span>01</span>
              The filmmaker cut
            </p>
            <div>
              <h2>For Filmmakers</h2>
              <p>
                Equipping independent filmmakers with the tools to connect, grow, and champion their
                work.
              </p>
            </div>
          </div>

          <div className="filmmaker-layout">
            <div className="film-strip-stage">
              <div className="film-sprockets film-sprockets-top" aria-hidden="true" />
              <div className="film-stock-label film-stock-label-top" aria-hidden="true">
                <span>FS · 35MM</span>
                <span>FRAME 01</span>
                <span>ARTIST PRINT</span>
              </div>

              <div
                className="submission-preview"
                role="img"
                aria-label="Filmspo submission tracker preview"
              >
                <img
                  key={activeFilmmaker}
                  src={activeFilmmakerFeature.screenshot}
                  alt={activeFilmmakerFeature.screenshotAlt}
                  className="preview-screenshot"
                />
              </div>

              <div className="film-stock-label film-stock-label-bottom" aria-hidden="true">
                <span>FILMSPO / EXP 24</span>
                <span>◀ EMULSION SIDE</span>
                <span>01A</span>
              </div>
              <div className="film-sprockets film-sprockets-bottom" aria-hidden="true" />
            </div>

            <div className="feature-stack">
              {filmmakerFeatures.map((feature, index) => {
                const Icon = feature.icon;
                return (
                  <article
                    key={feature.title}
                    className={`editorial-feature-card${
                      activeFilmmaker === index ? ' is-active' : ''
                    }`}
                    onClick={() => setActiveFilmmaker(index)}
                  >
                    <div className="feature-card-index">
                      {/* <span>{feature.number}</span> */}
                      <Icon aria-hidden="true" />
                    </div>
                    <div>
                      <h3>{feature.title}</h3>
                      <p>{feature.description}</p>
                    </div>
                  </article>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      <section id="festivals" className="festival-section section-shell">
        <div className="festival-light-beam" aria-hidden="true" />
        <div className="page-container festival-container">
          <div className="section-heading editorial-heading on-dark">
            <p className="section-kicker">
              <span>02</span>
              The festival cut
            </p>
            <div>
              <h2>For Festival Organizers</h2>
              <p>
                The only tool you need to manage submissions, communicate with filmmakers, and grow
                your festival.
              </p>
            </div>
          </div>
          <div className="projection-stage mobile-show">
            <div className="projection-beam" aria-hidden="true" />

            <div className="projection-screen">
              <div className="screen-housing" aria-hidden="true">
                <span>SCREEN 02</span>
                <span>FESTIVAL OPERATIONS</span>
              </div>
              <div
                className="portal-preview"
                role="img"
                aria-label="Filmspo festival management portal preview"
              >
                <img
                  key={activeFestival}
                  src={activeFestivalFeature.screenshot}
                  alt={activeFestivalFeature.screenshotAlt}
                  className="preview-screenshot"
                />
              </div>
              <div className="screen-pull" aria-hidden="true">
                <span />
              </div>
            </div>
          </div>

          <div className="festival-layout">
            <div className="festival-feature-list">
              {festivalFeatures.map((feature, index) => {
                const Icon = feature.icon;
                return (
                  <article
                    key={feature.title}
                    className={activeFestival === index ? 'is-active' : undefined}
                    onClick={() => setActiveFestival(index)}
                  >
                    <div className="festival-feature-icon">
                      <Icon aria-hidden="true" />
                    </div>
                    <div>
                      {/* <span>0{index + 1}</span> */}
                      <h3>{feature.title}</h3>
                      <p>{feature.description}</p>
                    </div>
                  </article>
                );
              })}
            </div>

            <div className="projection-stage desktop-show">
              <div className="projection-beam" aria-hidden="true" />

              <div className="projection-screen">
                <div className="screen-housing" aria-hidden="true">
                  <span>SCREEN 02</span>
                  <span>FESTIVAL OPERATIONS</span>
                </div>
                <div
                  className="portal-preview"
                  role="img"
                  aria-label="Filmspo festival management portal preview"
                >
                  <img
                    key={activeFestival}
                    src={activeFestivalFeature.screenshot}
                    alt={activeFestivalFeature.screenshotAlt}
                    className="preview-screenshot"
                  />
                </div>
                <div className="screen-pull" aria-hidden="true">
                  <span />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="features" className="journey-section section-shell">
        <div className="page-container">
          <div className="journey-intro">
            <p className="section-kicker">
              <span>03</span>
              Features with film in mind
            </p>
            <h2>
              <span>Check out our other features.</span>
            </h2>
            <p>
              Filmspo has an exhaustive list of useful tools for every level of filmmaker and
              festival organizer.
            </p>
          </div>

          <div className="journey-carousel">
            <div className="journey-carousel-track">
              {journey.map((step, index) => {
                const raw = index - activeJourney;
                const offset = (raw + journey.length) % journey.length;
                // normalise to -N..+N around 0
                const mid = journey.length / 2;
                const norm = offset > mid ? offset - journey.length : offset;
                const clamped = Math.max(-3, Math.min(3, norm));
                const hidden = Math.abs(norm) > 3;
                return (
                  <article
                    key={step.number}
                    className="journey-carousel-item"
                    data-offset={clamped}
                    aria-hidden={hidden}
                    onClick={() => !hidden && setActiveJourney(index)}
                  >
                    <div className="journey-frame">
                      <img src={step.screenshot} alt={step.screenshotAlt} />
                    </div>
                    <div className="journey-item-copy">
                      <h3>{step.title}</h3>
                      <p>{step.description}</p>
                    </div>
                  </article>
                );
              })}
            </div>

            <div className="journey-carousel-nav" aria-label="Browse features">
              <button onClick={prevJourney} aria-label="Previous">
                <ArrowRight style={{ transform: 'rotate(180deg)' }} />
              </button>
              <span aria-live="polite">
                {activeJourney + 1} / {journey.length}
              </span>
              <button onClick={nextJourney} aria-label="Next">
                <ArrowRight />
              </button>
            </div>
          </div>
        </div>
      </section>

      <section className="closing-section">
        <div className="closing-filmstrip" aria-hidden="true">
          {Array.from({ length: 12 }, (_, index) => (
            <span key={index} />
          ))}
        </div>
        <div className="page-container closing-content">
          <p className="section-kicker">Ready when you are</p>
          <h2>More than just a submission platform.</h2>
          <p>Start building a better community with fellow filmmakers and festival organizers.</p>
          <WaitlistTrigger source="closing" className="button button-gold button-large">
            Join the Waitlist
            <ArrowRight aria-hidden="true" />
          </WaitlistTrigger>
        </div>
      </section>
    </div>
  );
}
